import cx from "classnames";
import { ReactNode } from "react";
import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faExclamation, faCircle } from "@fortawesome/pro-solid-svg-icons";

import Icon from "common/components/Icon";

import styles from "../../styles/Timeline.module.less";

/**
 * Complete: Success state indicating completion
 * Active: Currently happening event
 * Error: Currently happening event, but something went wrong
 * Inactive: Some future event
 * Default: When none of the above apply, and a simple uniform timeline is needed
 */
export type Status = "complete" | "active" | "error" | "inactive" | "default";

type TimelineEventOpts = {
  status: Status;
  header: ReactNode;
  content: ReactNode;
  label?: ReactNode;
};

const defaultIcons: Record<Status, IconDefinition> = {
  complete: faCheck,
  active: faCircle,
  error: faCircle,
  inactive: faExclamation,
  default: faCircle,
};

type TimelineEventProps = TimelineEventOpts & {
  nextEvent?: TimelineEventOpts;
};

const TimelineEvent = ({
  status = "active",
  nextEvent,
  header,
  content,
  label,
}: TimelineEventProps) => {
  const nextStatus = nextEvent?.status;
  const icon = defaultIcons[status];

  return (
    <div className={cx(styles.event, styles[status])}>
      <div className={styles.display}>
        <div className={cx(styles.line, styles[`line-${status}`])} />
        <div className={cx(styles.line, styles.next, styles[`line-${nextStatus}`])} />
        <div className={cx(styles.dot, styles[`dot-${status}`])}>
          <Icon icon={icon} />
        </div>
      </div>
      <div className={styles.main}>
        {label && <div className={cx(styles.label, styles[`label-${status}`])}>{label}</div>}
        <div className={styles.header}>{header}</div>
        <div className={styles.content}>{content}</div>
      </div>
    </div>
  );
};

export type TimelinePropEventOpts = TimelineEventOpts & {
  key: string;
};

type TimelineProps = {
  size?: "s" | "m" | "l";
  events: TimelinePropEventOpts[];
};

const Timeline = ({ events, size = "m" }: TimelineProps) => (
  <div className={cx(styles.container, styles[size])}>
    {events.map(({ key, ...itemProps }, index) => (
      <TimelineEvent key={key} nextEvent={events[index + 1]} {...itemProps} />
    ))}
  </div>
);

export default Timeline;
